import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import products from '../data/products';
import stories from '../data/stories';

const useReveal = () => {
  useEffect(() => {
    const targets = document.querySelectorAll('.gc-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.15 }
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

// ─── 写真スライダー（1枚ずつ、自動切替） ─────────────
const Slider = ({ images, interval = 5000 }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, interval);
    return () => clearInterval(timer);
  }, [images.length, interval]);

  return (
    <div className="gc-slider">
      <div
        className="gc-slider__track"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {images.map((img, i) => (
          <div className="gc-slider__slide" key={i}>
            <img src={img.src} alt={img.alt} className="gc-slider__img" loading="lazy" />
          </div>
        ))}
      </div>
      <div className="gc-slider__dots">
        {images.map((_, i) => (
          <button
            key={i}
            className={`gc-slider__dot${i === current ? ' gc-slider__dot--active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`スライド${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// ─── 横スクロール写真ストリップ（無限ループ） ─────────
const ImageStrip = ({ images }) => (
  <div className="gc-strip">
    <div className="gc-strip__track">
      {[...images, ...images].map((img, i) => (
        <img key={i} src={img.src} alt={img.alt} className="gc-strip__img" loading="lazy" />
      ))}
    </div>
  </div>
);

// ─── セクション間の全幅写真（パララックス） ─────────────
const Interlude = ({ img }) => {
  const containerRef = useRef(null);
  const bgRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || !bgRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewHeight = window.innerHeight;
      if (rect.bottom > 0 && rect.top < viewHeight) {
        const progress = (viewHeight - rect.top) / (viewHeight + rect.height);
        bgRef.current.style.transform = `translateY(${(progress - 0.5) * -60}px) scale(1.05)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="gc-interlude" ref={containerRef}>
      <div
        className="gc-interlude__bg"
        ref={bgRef}
        style={{ backgroundImage: `url(${img})` }}
      />
    </div>
  );
};

// ─── データ ──────────────────────────────────────────

const storyItems = stories;

const aboutSliderImages = [
  { src: '/images/generated/slider_01_natadera.jpg', alt: '那谷寺の苔むした参道' },
  { src: '/images/generated/slider_02_yamanaka.jpg', alt: '山中温泉の渓谷' },
  { src: '/images/generated/slider_03_kutaniyaki.jpg', alt: '九谷焼の伝統工芸' },
  { src: '/images/generated/slider_04_onsen.jpg', alt: '山代温泉の古総湯' },
  { src: '/images/generated/slider_05_coffee_landscape.jpg', alt: 'コーヒーのある南加賀の風景' },
];

const galleryImages = [
  { src: '/images/generated/gallery_01_tsurusenke.jpg', alt: '鶴仙渓の遊歩道' },
  { src: '/images/generated/gallery_02_korogi.jpg', alt: 'こおろぎ橋' },
  { src: '/images/generated/gallery_03_kutani_workshop.jpg', alt: '九谷焼の工房' },
  { src: '/images/generated/gallery_04_natadera_autumn.jpg', alt: '那谷寺の紅葉' },
  { src: '/images/generated/gallery_05_pour_coffee.jpg', alt: 'ハンドドリップコーヒー' },
];

// ─── セクション ──────────────────────────────────────

let isFirstLoad = true;

const Hero = () => {
  const bgRef = useRef(null);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const delay = isFirstLoad ? 2600 : 200;
    isFirstLoad = false;

    const bgTimer = setTimeout(() => {
      if (bgRef.current) bgRef.current.classList.add('is-visible');
    }, Math.max(delay - 400, 100));

    const textTimer = setTimeout(() => setAnimating(true), delay);

    const handleScroll = () => {
      if (bgRef.current) {
        bgRef.current.style.transform = `translateY(${window.scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(bgTimer);
      clearTimeout(textTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const catchLine1 = '那谷の森へ、';
  const catchLine2 = '心は向かう。';

  return (
    <section className="gc-hero">
      <div
        className="gc-hero__bg"
        ref={bgRef}
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/generated/hero_bg.jpg)` }}
      ></div>
      <div className="gc-hero__inner">
        <span className={`gc-hero__en gc-hero__anim${animating ? ' is-visible' : ''}`}>
          Specialty Coffee from Komatsu, Ishikawa
        </span>
        <h1 className="gc-hero__catch">
          {catchLine1.split('').map((char, i) => (
            <span
              key={`a${i}`}
              className={`gc-hero__char${animating ? ' is-visible' : ''}`}
              style={{ transitionDelay: `${0.3 + i * 0.07}s` }}
            >
              {char}
            </span>
          ))}
          <br />
          {catchLine2.split('').map((char, i) => (
            <span
              key={`b${i}`}
              className={`gc-hero__char${animating ? ' is-visible' : ''}`}
              style={{ transitionDelay: `${0.8 + i * 0.07}s` }}
            >
              {char}
            </span>
          ))}
        </h1>
        <p className={`gc-hero__sub gc-hero__anim${animating ? ' is-visible' : ''}`} style={{ transitionDelay: '1.5s' }}>
          石川・小松から届く、こだわりの一杯。<br />
          人生の余白時間を、コーヒーとともに。
        </p>
      </div>
      <span className={`gc-hero__scroll gc-hero__anim${animating ? ' is-visible' : ''}`} style={{ transitionDelay: '1.9s' }}>
        scroll
      </span>
    </section>
  );
};

const About = () => (
  <section id="about" className="gc-section gc-about">
    <div className="gc-about__inner">
      <div className="gc-reveal">
        <span className="gc-section-en">About</span>
        <h2 className="gc-section-title">余白のある暮らしを、<br />一杯のコーヒーから。</h2>
      </div>
      <p className="gc-about__lead gc-reveal gc-reveal--delay-1">
        GURI COFFEEは、石川県加賀・小松エリアを拠点とするファントムロースター。
        実店舗を持たないからこそ、豆の選定から焙煎、お届けまで、
        すべてに向き合う時間をつくることができます。
      </p>
      <p className="gc-about__detail gc-reveal gc-reveal--delay-2">
        慌ただしい日常の中に、ほんの少しの余白を。
        コーヒーを淹れる時間、その香りに包まれる瞬間が、
        あなたの「余白時間」になれますように。
      </p>
      <div className="gc-reveal gc-reveal--delay-3" style={{ marginTop: '2.5rem' }}>
        <Link to="/about" className="gc-btn">詳しく見る</Link>
      </div>
    </div>
  </section>
);

const LineUp = () => (
  <section id="lineup" className="gc-section gc-lineup">
    <div className="gc-lineup__inner">
      <div className="gc-reveal">
        <span className="gc-section-en">Line Up</span>
        <h2 className="gc-section-title">コーヒー豆</h2>
      </div>
      <div className="gc-lineup__grid">
        {products.map((p) => (
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="gc-product-card gc-reveal"
            key={p.name}
          >
            <div className="gc-product-card__img-wrap">
              <img className="gc-product-card__img" src={p.img} alt={p.nameJa} loading="lazy" />
            </div>
            <div className="gc-product-card__body">
              <span className="gc-product-card__en">{p.name}</span>
              <h3 className="gc-product-card__name">{p.nameJa}</h3>
              {p.farm && <span className="gc-product-card__farm">{p.farm}</span>}
              <p className="gc-product-card__desc">{p.desc}</p>
              <div className="gc-product-card__footer">
                <span className="gc-product-card__weight">{p.weight}</span>
                <span className="gc-product-card__price">{p.price}</span>
              </div>
            </div>
          </a>
        ))}
      </div>
      <div className="gc-reveal" style={{ textAlign: 'center', marginTop: '3rem' }}>
        <a
          href="https://guricoffee.base.shop/"
          target="_blank"
          rel="noopener noreferrer"
          className="gc-btn gc-btn--light"
        >
          Online Store
        </a>
      </div>
    </div>
  </section>
);

const Story = () => (
  <section id="story" className="gc-section gc-story">
    <div className="gc-reveal gc-story__header">
      <span className="gc-section-en">Story</span>
      <h2 className="gc-section-title">物語</h2>
    </div>
    {storyItems.map((item) => (
      <React.Fragment key={item.en}>
        <div className="gc-story__img-block gc-reveal">
          <img className="gc-story__img-full" src={item.img} alt={item.title} loading="lazy" />
        </div>
        <div className="gc-story__text-block gc-reveal">
          <span className="gc-section-en">{item.en}</span>
          <h3 className="gc-section-title">{item.title}</h3>
          <p className="gc-story__text">{item.text}</p>
        </div>
      </React.Fragment>
    ))}
  </section>
);

const AreaGuide = () => (
  <section id="area" className="gc-section gc-area">
    <div className="gc-area__header gc-reveal">
      <span className="gc-section-en">Model Course</span>
      <h2 className="gc-section-title">南加賀をめぐる</h2>
      <p className="gc-area__lead">
        奇岩と工芸、湯の余白——<br />
        GURI COFFEEが生まれた南加賀エリアを、一日かけてめぐるおすすめコース。
      </p>
    </div>
    <div className="gc-area__course-img gc-reveal">
      <img
        src="/images/model-course.png"
        alt="南加賀モデルコース：那谷寺→ゆのくにの森→こおろぎ橋・鶴仙渓→山代古総湯"
        className="gc-area__course-map"
        loading="lazy"
      />
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="gc-section gc-contact">
    <div className="gc-contact__inner gc-reveal">
      <span className="gc-section-en">Contact</span>
      <h2 className="gc-section-title gc-section-title--light">お問い合わせ</h2>
      <p className="gc-contact__text">
        コーヒー豆のご注文、法人ギフトのご相談など、<br />
        お気軽にお問い合わせください。
      </p>
      <p className="gc-contact__text">
        <strong>ドリップバッグ</strong>や<strong>贈り物としてのご用意</strong>も、<br />
        ご相談いただければ受注にて承っています。<br />
        （店頭に並ぶ既製のセット商品はご用意していません）
      </p>
      <a href="mailto:guri.coffee4945@gmail.com" className="gc-btn gc-btn--gold">
        Contact Us
      </a>
    </div>
  </section>
);

const MapSection = () => (
  <section id="map" className="gc-map-section">
    <div className="gc-map-section__inner gc-reveal">
      <span className="gc-section-en">Map</span>
      <h2 className="gc-section-title gc-section-title--light">エリアマップ</h2>
      <p className="gc-map-section__text">
        石川県小松市・加賀市エリア
      </p>
    </div>
    <div className="gc-map-section__embed">
      <iframe
        src="https://maps.google.com/maps?q=%E9%82%A3%E8%B0%B7%E5%AF%BA%2C%E7%9F%B3%E5%B7%9D%E7%9C%8C%E5%B0%8F%E6%9D%BE%E5%B8%82&t=&z=12&ie=UTF8&iwloc=&output=embed"
        width="100%"
        height="400"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="南加賀エリアマップ"
      ></iframe>
    </div>
  </section>
);

// ─── メインコンテンツ ────────────────────────────────
const MainContent = () => {
  useReveal();

  return (
    <main>
      <Hero />
      <About />
      <Slider images={aboutSliderImages} />
      <LineUp />
      <Interlude img="/images/generated/interlude.jpg" />
      <Story />
      <ImageStrip images={galleryImages} />
      <AreaGuide />
      <Contact />
      <MapSection />
    </main>
  );
};

export default MainContent;
